<template>
    <v-tooltip top>
        <template v-slot:activator="{ on }">
            <v-btn v-on="on" color="primary" small class="mb-2 mr-2" fab :loading="pdfWorking" :disabled="pdfWorking"
                @click="onBuildClick(
                title,
                subtitle,
                content,
                headers,
                filename
            )">
                <v-icon color="white">mdi-file-pdf-box</v-icon></v-btn>
        </template>
        <span>{{ $t('system.labels.exportToPdf') }}</span>
    </v-tooltip>
</template>

<script>
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { isHeaderSelectedForExport } from "@/utils/relatorioExportProfiles";

/**
 * As fontes embutidas do jsPDF são Latin-1 (WinAnsi): fora disso o texto sai embaralhado,
 * com as letras espaçadas. Δ, − e — aparecem em cabeçalho e célula desta base, então
 * trocamos por equivalentes ASCII antes de escrever no PDF.
 */

/** Espaços especiais (NBSP e afins) montados por código: no fonte seriam invisíveis. */
const ESPACOS_ESPECIAIS = new RegExp(
    [0x00A0, 0x202F, 0x2009].map((c) => String.fromCharCode(c)).join('|'),
    'g',
);
const SUBSTITUICOES_PDF = [
    [/Δ\s*/g, 'Delta '],
    [/[−–—]/g, '-'],
    [/…/g, '...'],
    [/[‘’]/g, "'"],
    [/[“”]/g, '"'],
    [ESPACOS_ESPECIAIS, ' '],
];
function textoPdf(valor) {
    if (valor == null) return '';
    let texto = String(valor);
    for (const [busca, troca] of SUBSTITUICOES_PDF) texto = texto.replace(busca, troca);
    // Fora do Latin-1 a fonte padrão não tem glifo: sumir com o caractere é melhor do que
    // embaralhar a linha inteira.
    let saida = '';
    for (const ch of texto) saida += ch.charCodeAt(0) <= 255 ? ch : '';
    return saida;
}

/** "#1976d2" → [25, 118, 210] (cor de preenchimento do jsPDF). */
function hexToRgb(hex) {
    const m = String(hex || '').trim().match(/^#?([0-9a-f]{6})$/i);
    if (!m) return [120, 120, 120];
    const n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const PDF_LEFT = 1.5;
const PDF_RIGHT = 28.5;
const PDF_BOTTOM_MARGIN = 1.2;

/** Pula de página quando a altura pedida não cabe; devolve o Y para desenhar. */
function ensureSpace(doc, y, height) {
    if (y + height > doc.internal.pageSize.getHeight() - PDF_BOTTOM_MARGIN) {
        doc.addPage();
        return 1.5;
    }
    return y;
}

/** Legenda em linha (quadrado colorido + texto); devolve o Y abaixo dela. */
function drawLegend(doc, legend, y) {
    let x = PDF_LEFT;
    doc.setFontSize(7);
    (legend || []).forEach((item) => {
        const label = textoPdf(item.label);
        doc.setFillColor(...hexToRgb(item.color));
        doc.rect(x, y - 0.22, 0.25, 0.25, 'F');
        doc.setTextColor(60, 60, 60);
        doc.text(label, x + 0.35, y);
        x += 0.35 + doc.getTextWidth(label) + 0.6;
    });
    doc.setTextColor(0, 0, 0);
    return y + 0.45;
}

/**
 * Barras agrupadas: `{ kind: 'bars', labels, series: [{ label, color, values }],
 * markers?: [cor | null] (faixa sob cada grupo), legend?, heightCm?,
 * maxLabel? (texto do topo do eixo, ex.: valor monetário formatado) }`. Cada série aceita
 * `valueLabels?: [texto]` escrito acima da barra. A largura da barra é limitada para poucos
 * grupos não gerarem barras gigantes.
 */
function drawBarsChart(doc, chart, startY) {
    const height = Number(chart.heightCm) || 4.5;
    const labels = chart.labels || [];
    const series = chart.series || [];
    const hasValueLabels = series.some((s) => Array.isArray(s.valueLabels));
    const headroom = hasValueLabels ? 0.4 : 0;
    const y0 = ensureSpace(doc, startY, height + headroom + 1.4) + headroom;
    const left = PDF_LEFT + 0.6;
    const bottom = y0 + height;
    const max = Math.max(1, ...series.flatMap((s) => (s.values || []).map((v) => Number(v) || 0)));
    const groupW = (PDF_RIGHT - left) / Math.max(1, labels.length);
    const barW = Math.min(2.5, (groupW * 0.8) / Math.max(1, series.length));
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.01);
    doc.line(left, bottom, PDF_RIGHT, bottom);
    doc.line(left, y0, left, bottom);
    doc.setFontSize(6);
    doc.text(textoPdf(chart.maxLabel != null ? chart.maxLabel : String(max)), left - 0.15, y0 + 0.15, { align: 'right' });
    doc.text('0', left - 0.15, bottom, { align: 'right' });
    labels.forEach((label, i) => {
        const center = left + i * groupW + groupW / 2;
        const gx = center - (barW * series.length) / 2;
        series.forEach((s, k) => {
            const v = Number((s.values || [])[i]) || 0;
            if (v <= 0) return;
            const h = (v / max) * height;
            doc.setFillColor(...hexToRgb(s.color));
            doc.rect(gx + k * barW, bottom - h, barW, h, 'F');
            const valueLabel = (s.valueLabels || [])[i];
            if (valueLabel) {
                doc.text(textoPdf(valueLabel), gx + k * barW + barW / 2, bottom - h - 0.1, { align: 'center' });
            }
        });
        const marker = (chart.markers || [])[i];
        if (marker) {
            doc.setFillColor(...hexToRgb(marker));
            doc.rect(center - groupW * 0.4, bottom + 0.05, groupW * 0.8, 0.12, 'F');
        }
        doc.text(textoPdf(label), left + i * groupW + groupW / 2, bottom + 0.45, { align: 'center' });
    });
    return drawLegend(doc, chart.legend, bottom + 0.9) + 0.2;
}

/**
 * Linhas do tempo (Gantt): `{ kind: 'timeline', start, end (minutos), ticks: [{ at, label }],
 * rows: [{ label, segments: [{ ini, fim, color }] }], legend? }`. Quebra de página entre linhas
 * repete o eixo.
 */
function drawTimelineChart(doc, chart, startY) {
    const rowH = 0.38;
    const labelW = 4.5;
    const left = PDF_LEFT + labelW;
    const width = PDF_RIGHT - left;
    const span = Math.max(1, Number(chart.end) - Number(chart.start));
    const xOf = (m) => left + ((Number(m) - Number(chart.start)) / span) * width;
    const drawAxis = (y) => {
        doc.setFontSize(6);
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.01);
        (chart.ticks || []).forEach((tick) => {
            const x = xOf(tick.at);
            doc.text(textoPdf(tick.label), x, y, { align: 'center' });
        });
        return y + 0.2;
    };
    let y = ensureSpace(doc, startY, 0.6 + rowH * Math.min(3, (chart.rows || []).length));
    y = drawAxis(y);
    const axisTop = y;
    (chart.rows || []).forEach((row) => {
        if (y + rowH > doc.internal.pageSize.getHeight() - PDF_BOTTOM_MARGIN) {
            doc.addPage();
            y = drawAxis(1.5);
        }
        doc.setFontSize(6.5);
        doc.text(textoPdf(row.label), PDF_LEFT, y + rowH * 0.7);
        doc.setFillColor(236, 239, 241);
        doc.rect(left, y + 0.06, width, rowH - 0.12, 'F');
        (row.segments || []).forEach((seg) => {
            const x1 = xOf(seg.ini);
            const x2 = xOf(seg.fim);
            doc.setFillColor(...hexToRgb(seg.color));
            doc.rect(x1, y + 0.06, Math.max(0.03, x2 - x1), rowH - 0.12, 'F');
        });
        y += rowH;
    });
    doc.setDrawColor(210, 210, 210);
    (chart.ticks || []).forEach((tick) => {
        const x = xOf(tick.at);
        doc.line(x, axisTop, x, Math.max(axisTop, y));
    });
    return drawLegend(doc, chart.legend, y + 0.45) + 0.2;
}

function drawChart(doc, chart, startY) {
    if (chart && chart.kind === 'timeline') return drawTimelineChart(doc, chart, startY);
    return drawBarsChart(doc, chart || {}, startY);
}

const yieldToUi = () => new Promise((r) => setTimeout(r, 0));
const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));

/** Faixas 0–100 do export PDF (fetch → montagem → gravação), linear e contínuo. */
const PDF_P_FETCH_END = 90;
const PDF_P_BUILD_END = 95;
const PDF_P_BEFORE_SAVE = 99;

export default {
    name: "ExportTablePdf",
    data: () => ({
        pdfWorking: false,
    }),
    props: {
        content: {
            type: Array,
            default() {
                return [];
            }
        },
        headers: {
            type: Array,
            default() {
                return [];
            }
        },
        title: {
            type: String,
            default() {
                return this.$t('system.labels.title')
            }
        },
        subtitle: {
            type: String,
            default() {
                return ''
            }
        },
        filename: {
            type: String,
            default() {
                return 'exportpdftable'
            }
        },
        /** Se definido, os dados do PDF vêm desta função assíncrona (exportação sob demanda). */
        contentLoader: {
            type: Function,
            default: null
        },
        /** Monta linhas em fatias com pausas para a UI (grandes volumes). */
        deferProcessing: {
            type: Boolean,
            default: false
        },
        unwrapObject: {
            type: Boolean,
            default() {
                return false
            }
        },
        objectItemsToShow: {
            type: Number,
            default() {
                return 0
            }
        },
        fontSize: {
            type: Number,
            default() {
                return 8
            }
        },
        horizontalPageBreak: {
            type: Boolean,
            default() {
                return false
            }
        },
        /**
         * Várias tabelas no mesmo PDF. Cada item: { sectionTitle?, headers, content } ou, para
         * um gráfico, { sectionTitle?, chart } (ver drawBarsChart / drawTimelineChart).
         * Quando preenchido, substitui o par único content/headers.
         */
        sections: {
            type: Array,
            default: null,
        },
        /** Função chamada na hora da exportação para obter sections (evita props desatualizadas). */
        sectionsProvider: {
            type: Function,
            default: null,
        },
    },
    methods: {
        filterHeaders(headers) {
            // Mesma regra do CSV/extenso: to_export manda; show só é fallback.
            // Não excluir por show=false quando to_export=1 (headers sync nascem com to_show=0).
            return (headers || []).filter((h) => {
                if (!h || !h.value) return false;
                if (h.value === 'actions' || h.value === 'data-table-expand') return false;
                if (h.text === this.$t('realTimeKmReport.headers.actions')) return false;
                if (h.align === ' d-none') return false;
                return isHeaderSelectedForExport(h);
            });
        },
        resolveSections(content, headers) {
            if (typeof this.sectionsProvider === 'function') {
                const fromProvider = this.sectionsProvider();
                if (Array.isArray(fromProvider) && fromProvider.length) {
                    return fromProvider;
                }
            }
            if (Array.isArray(this.sections) && this.sections.length) {
                return this.sections;
            }
            return [{
                sectionTitle: '',
                headers: headers || [],
                content: Array.isArray(content) ? content : [],
            }];
        },
        rowsToTableBody(rows, filteredHeaders) {
            return (rows || []).map((obj) => filteredHeaders.map((column) => this.cellValue(obj, column)));
        },
        cellValue(obj, column) {
            // `exportFormat` no header deixa o PDF sair com a mesma formatação da tela
            // (HH:MM, sinal do delta) em vez do valor cru do item.
            if (typeof column.exportFormat === 'function') {
                return textoPdf(column.exportFormat(obj[column.value], obj));
            }
            if (this.unwrapObject && typeof obj[column.value] === 'object') {
                let fullValue = "";
                Object.values(obj[column.value]).forEach((v, i) => {
                    if (this.objectItemsToShow > 0 && i < this.objectItemsToShow) {
                        fullValue += `${v} `;
                    }
                });
                return fullValue;
            }
            return obj[column.value] != null ? textoPdf(obj[column.value]) : '';
        },
        async buildTableBodyChunked(rows, filteredHeaders, onSliceProgress) {
            const tableContent = [];
            const chunk = 400;
            const n = rows.length;
            for (let i = 0; i < rows.length; i += chunk) {
                const slice = rows.slice(i, i + chunk);
                for (let j = 0; j < slice.length; j++) {
                    const obj = slice[j];
                    tableContent.push(filteredHeaders.map((column) => this.cellValue(obj, column)));
                }
                if (typeof onSliceProgress === 'function' && n > 0) {
                    onSliceProgress(Math.min(1, (i + slice.length) / n));
                }
                if (this.deferProcessing && i + chunk < rows.length) {
                    await yieldToUi();
                }
            }
            return tableContent;
        },
        async onBuildClick(title, subtitle, content, headers, filename) {
            this.pdfWorking = true;
            const pdfDetail = this.$t('system.labels.exportToPdf');
            this.$emit('export-loading', {
                loading: true,
                message: pdfDetail,
                progress: 0,
            });
            try {
                await this.build(title, subtitle, content, headers, filename);
            } finally {
                this.pdfWorking = false;
                this.$emit('export-loading', { loading: false });
            }
        },
        emitExportProgress(percent, message) {
            this.$emit('export-loading', {
                loading: true,
                message: message || this.$t('system.labels.exportToPdf'),
                progress: Math.min(100, Math.max(0, Math.round(percent))),
            });
        },
        async build(title, subtitle, content, headers, filename) {
            const pdfDetail = this.$t('system.labels.exportToPdf');
            const report = (pct) => this.emitExportProgress(pct, pdfDetail);

            let rows = content;
            if (typeof this.contentLoader === 'function') {
                try {
                    rows = await this.contentLoader((fetchPct) => {
                        const f = Math.min(100, Math.max(0, Number(fetchPct))) / 100;
                        report(Math.round(f * PDF_P_FETCH_END));
                    });
                } catch (e) {
                    rows = [];
                }
            } else {
                report(PDF_P_FETCH_END);
            }
            if (!Array.isArray(rows)) {
                rows = [];
            }

            report(PDF_P_FETCH_END);
            this.mensagemProgressoDialog = this.$t('operation.exportingPdfWait');
            this.progressoDialog = true;

            const date = new Date();
            const doc = new jsPDF({
                orientation: "l",
                unit: "cm",
                //format: [300, 700],
                format: "a4",
            });
            doc.text(textoPdf(title), 1.5, 1.5);
            doc.text(date.toLocaleString(), 23.0, 1.5);
            doc.setFontSize(10);
            doc.setLineWidth(0.01);
            doc.line(1.4, 1.8, 29.0, 1.8);
            const subtitleText = subtitle != null ? String(subtitle).trim() : '';
            let startY = 2.2;
            if (subtitleText) {
                // splitTextToSize respeita quebras explícitas e ainda parte a linha comprida
                // na largura útil: sem isso um subtítulo longo sai cortado na borda da página.
                // Escrevemos linha a linha porque um cabeçalho extenso (a lista de linhas da
                // consulta, por exemplo) pode passar do rodapé e precisa continuar na página
                // seguinte, em vez de ser escrito por cima da margem.
                const linhas = doc.splitTextToSize(textoPdf(subtitleText), 27.0);
                const alturaLinha = 0.45;
                const limiteY = doc.internal.pageSize.getHeight() - 1.5;
                let y = 2.4;
                for (const linha of linhas) {
                    if (y > limiteY) {
                        doc.addPage();
                        y = 1.5;
                    }
                    doc.text(linha, 1.5, y);
                    y += alturaLinha;
                }
                startY = y + 0.3;
            }

            const sections = this.resolveSections(rows, headers);
            const buildSpan = PDF_P_BUILD_END - PDF_P_FETCH_END;
            const sectionCount = Math.max(1, sections.length);

            for (let s = 0; s < sections.length; s++) {
                const section = sections[s] || {};
                const sectionRows = Array.isArray(section.content) ? section.content : [];
                const filteredHeaders = this.filterHeaders(section.headers);

                if (section.sectionTitle) {
                    startY = ensureSpace(doc, startY, section.chart ? 2.5 : 1.2);
                    doc.setFontSize(11);
                    doc.text(textoPdf(section.sectionTitle), 1.5, startY);
                    startY += 0.55;
                }

                if (section.chart) {
                    startY = drawChart(doc, section.chart, startY) + 0.3;
                    doc.setFontSize(10);
                    continue;
                }

                let tableContent;
                if (this.deferProcessing) {
                    await nextFrame();
                    tableContent = await this.buildTableBodyChunked(sectionRows, filteredHeaders, (fr) => {
                        const base = PDF_P_FETCH_END + (s / sectionCount) * buildSpan;
                        report(base + Math.round(fr * (buildSpan / sectionCount)));
                    });
                    await yieldToUi();
                } else {
                    tableContent = this.rowsToTableBody(sectionRows, filteredHeaders);
                }

                autoTable(doc, {
                    styles: { fontSize: this.fontSize },
                    head: filteredHeaders.length ? [filteredHeaders.map((h) => textoPdf(h.text))] : [],
                    body: tableContent,
                    startY,
                    pageBreak: 'auto',
                    horizontalPageBreak: this.horizontalPageBreak,
                });

                if (doc.lastAutoTable && doc.lastAutoTable.finalY != null) {
                    startY = doc.lastAutoTable.finalY + 0.7;
                }
            }

            report(PDF_P_BUILD_END);

            const fileFullName =
                filename +
                date.getFullYear() +
                ("0" + (date.getMonth() + 1)).slice(-2) +
                ("0" + date.getDate()).slice(-2) +
                ("0" + date.getHours()).slice(-2) +
                ("0" + date.getMinutes()).slice(-2) +
                ("0" + date.getSeconds()).slice(-2) +
                ".pdf";
            if (this.deferProcessing) {
                await yieldToUi();
            }
            report(PDF_P_BEFORE_SAVE);
            doc.save(fileFullName);
            report(100);
            this.mensagemProgressoDialog = "";
            this.progressoDialog = false;
        },
    }
}
</script>